import { isValidEmail } from '@common/utils/isValidEmail';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { THIN_SCROLLBAR_STYLING } from '@/constants';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { sendQuoteFormLink } from '@/services/EmailService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import QuoteLinkSentBody from './QuoteLinkSentBody';

export interface FormData {
    practiceArea: string;
    matterType: string;
    selectedTemplateId: string;
    assignedLawyer: number | undefined;
    lawyerName: string;
    lawyerEmail: string;
    quoteFormLink: string;
    clientFirstName: string;
    clientLastName: string;
    clientEmail: string;
    ccPerson: string[];
    signature: string;
}

export interface QuoteSentErrors {
    practiceArea: string;
    matterType: string;
    selectedTemplateId: string;
    clientFirstName: string;
    clientLastName: string;
    clientEmail: string;
    assignedLawyer: string;
}

interface Props {
    onClose(): void;
}

export default function QuoteLinkSentModal({ onClose }: Props) {
    const [isCloseAlert, setIsCloseAlert] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { activeLawFirmId } = useLawFirmContext();

    const { data: selectedLawFirm } = useLawFirm(activeLawFirmId);

    const [formData, setFormData] = useState<FormData>({
        practiceArea: '',
        matterType: '',
        selectedTemplateId: '',
        assignedLawyer: 0,
        lawyerName: '',
        lawyerEmail: '',
        quoteFormLink: '',
        clientFirstName: '',
        clientLastName: '',
        clientEmail: '',
        ccPerson: [],
        signature: '',
    });
    const [errors, setErrors] = useState<QuoteSentErrors>({
        practiceArea: '',
        matterType: '',
        selectedTemplateId: '',
        clientFirstName: '',
        clientLastName: '',
        clientEmail: '',
        assignedLawyer: '',
    });

    const resetFormData = () => {
        setFormData({
            practiceArea: '',
            matterType: '',
            selectedTemplateId: '',
            assignedLawyer: 0,
            quoteFormLink: '',
            clientFirstName: '',
            clientLastName: '',
            lawyerName: '',
            lawyerEmail: '',
            clientEmail: '',
            ccPerson: [],
            signature: '',
        });

        setErrors({
            practiceArea: '',
            matterType: '',
            selectedTemplateId: '',
            clientFirstName: '',
            assignedLawyer: '',
            clientLastName: '',
            clientEmail: '',
        });
    };

    const handleClose = () => {
        resetFormData();
        onClose();
    };
    const validateForm = () => {
        let isValid = true;
        const newErrors = {
            practiceArea: '',
            matterType: '',
            selectedTemplateId: '',
            assignedLawyer: '',
            clientFirstName: '',
            clientLastName: '',
            clientEmail: '',
        };
        if (!formData.practiceArea) {
            newErrors.practiceArea = 'Practice area is required';
            isValid = false;
        }
        if (!formData.matterType) {
            newErrors.matterType = 'Matter type is required';
            isValid = false;
        }
        if (!formData.selectedTemplateId) {
            newErrors.selectedTemplateId = 'Template is required';
            isValid = false;
        }
        if (!formData.clientFirstName.trim()) {
            newErrors.clientFirstName = 'First name is required';
            isValid = false;
        }
        if (!formData.clientLastName.trim()) {
            newErrors.clientLastName = 'Last name is required';
            isValid = false;
        }
        if (!formData.clientEmail.trim()) {
            newErrors.clientEmail = 'Email is required';
            isValid = false;
        } else if (!isValidEmail(formData.clientEmail)) {
            newErrors.clientEmail = 'Invalid email format.';
            isValid = false;
        }
        if (!formData.assignedLawyer) {
            newErrors.assignedLawyer = 'Assigned user is required';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const isFormHasChanges = () => {
        return Object.values(formData).some((value) => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'number') return value !== 0;
            return value !== '';
        });
    };

    const handleSubmit = async () => {
        if (!selectedLawFirm || !validateForm()) return;

        try {
            setIsLoading(true);
            await sendQuoteFormLink({
                lawyerName: formData.lawyerName,
                lawyerEmail: formData.lawyerEmail,
                clientFirstName: formData.clientFirstName,
                clientEmail: formData.clientEmail,
                ccEmails: formData.ccPerson,
                quoteFormLink: formData.quoteFormLink,
                signature: formData.signature,
                lawFirmId: selectedLawFirm.lawFirmId,
            });
            successSnackbar('Quote Form Link Sent Successfully.');
            handleClose();
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            errorSnackbar('Failed to Send Quote Form Link.');
            console.error('Error submitting form data:', error);
        }
    };

    return (
        <>
            <Dialog
                open
                maxWidth={false}
                PaperProps={{
                    sx: {
                        borderRadius: '10px',
                        width: '90vW',
                    },
                }}
            >
                <div
                    style={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        color: 'white',
                        padding: '16px 20px',
                        textAlign: 'center',
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontSize: '20px',
                            fontWeight: 500,
                            marginBottom: '4px',
                            paddingBottom: '0px',
                        }}
                    >
                        Send Quote Form Link
                    </DialogTitle>
                    <DialogTitle
                        sx={{
                            opacity: 0.9,
                            fontSize: '13px',
                            padding: '0px',
                        }}
                    >
                        Set up a new transaction with client details and document configuration
                    </DialogTitle>
                </div>
                <DialogContent sx={THIN_SCROLLBAR_STYLING}>
                    <Box sx={{ width: '100%' }}>
                        <Box sx={THIN_SCROLLBAR_STYLING}>
                            <QuoteLinkSentBody
                                formData={formData}
                                setFormData={setFormData}
                                errors={errors}
                                setErrors={setErrors}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (isFormHasChanges()) {
                                setIsCloseAlert(true);
                            } else {
                                handleClose();
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isLoading}
                        variant="contained"
                        onClick={handleSubmit}
                        autoFocus
                    >
                        Finish
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <AlertDialog
                open={isCloseAlert}
                handleClose={() => setIsCloseAlert(false)}
                handleConfirm={handleClose}
                title="Are you sure?"
                message="Closing the popup will result in loss of any data that you have filled up. Are you sure you want to close the popup?"
            />
        </>
    );
}
